/**
 * Las claves de caché del módulo Drop, en un solo lugar.
 *
 * ── POR QUÉ EXISTE ─────────────────────────────────────────────────────────────────────────────
 * Un aviso en vivo refresca la pantalla invalidando su clave. Si la clave que invalida no es la
 * que la pantalla usa, el aviso llega y no pasa nada — y no falla nada, así que nadie se entera.
 * Pasó dos veces:
 *
 *   · `drop-saldo` en vez de `drop-mi-saldo` (21-sep-2026). El founder: «no llegó la recarga en
 *     tiempo real, tuve que actualizar».
 *   · `drop-mi-progreso`, que dejó de existir cuando se retiró el checklist del Inicio del Drop, y
 *     siguió invalidándose un día más.
 *
 * La primera vez se arregló con un candado que comparaba contra una lista ESCRITA A MANO en el
 * test. Esa lista incluía `drop-mi-progreso`: el candado pasaba en verde mientras la clave muerta
 * seguía ahí. Un candado que se mantiene a mano es una copia más para desincronizar.
 *
 * ⭐ ACÁ LA REGLA LA APLICA EL COMPILADOR. Las pantallas y el mapa de avisos usan estas constantes;
 *    una clave que no existe no compila, y una que se retira rompe en el lugar exacto donde quedó
 *    escrita. No hace falta acordarse de nada.
 *
 * 🪤 EL VALOR ES EL STRING DE SIEMPRE, y eso importa: `useDatosCacheados` y React Query comparan la
 *    clave por VALOR. Cambiar el texto de una de estas constantes no es renombrar una variable —
 *    es que la pantalla y el aviso dejen de hablar de lo mismo.
 */
export const CLAVES_DROP = {
  /** Las novedades vigentes para el negocio (la tarjeta del Inicio y el muro). */
  novedades: 'drop-novedades',
  /** El aviso «Importante» que toca mostrar al abrir el panel. */
  avisoDeNovedad: 'drop-aviso-de-novedad',
  /** La lista de novedades de Smarty HQ, con sus métricas. */
  novedadesDeSmarty: 'drop-novedades-de-smarty',
  /** Saldo de la billetera del revendedor (encabezado y página de Billetera). */
  miSaldo: 'drop-mi-saldo',
  /** Movimientos del libro del revendedor. */
  movimientos: 'drop-movimientos',
  /** La liquidación del drop por día, con los depósitos del courier (26-oct-2026). */
  liquidacionPorDia: 'drop-liquidacion-por-dia',
  /** Recargas declaradas por el revendedor. */
  misRecargas: 'drop-mis-recargas',
  /** Pedidos del revendedor al almacén. */
  misPedidos: 'drop-mis-pedidos',
  /** Previo del encargo de una venta (cuánto cuesta pedirla). */
  previoDeVenta: 'drop-previo-venta',
  /** Si un contra entrega que no cobra lo suficiente necesita prepago, y cuánto (20261019233100). */
  prepagoDeLaVenta: 'drop-prepago-de-la-venta',
  /** Los días de entrega de GSG de una venta, lo elegido y cómo va su registro (`consultaDeLaEntregaElegible`). */
  entregaElegible: 'drop-entrega-elegible',
  /** Estado del cumplimiento de una venta de Drop. */
  cumplimiento: 'drop-cumplimiento',
  /** El almacén que despacha un pedido (no el proveedor). 22-oct-2026. */
  almacenQueDespacha: 'drop-almacen-que-despacha',
  /** Cobros que recibió Smarty (Smarty HQ). */
  cobros: 'drop-cobros',
  /** Pagos que el almacén tiene por revisar. */
  pagosPorRevisar: 'drop-pagos-por-revisar',
  /** Billetera de la plataforma (Smarty HQ). */
  billeteraDeLaPlataforma: 'drop-billetera-plataforma',
  /** Si este negocio es proveedor (decide qué muestra el menú). */
  soyProveedor: 'drop-soy-proveedor',
  /** La ficha del proveedor, para el almacén. */
  miProveedor: 'drop-mi-proveedor',
  /** Permisos que el almacén le dio a un revendedor. */
  permisos: 'drop-permisos',
  /** Solicitudes de permiso que el almacén tiene pendientes. */
  solicitudesDelAlmacen: 'drop-solicitudes-del-almacen',
  /** Si el revendedor autorizó que su billetera se cobre sola. */
  preautorizacion: 'drop-preautorizacion',
  /** Lo que el almacén tiene por cobrar (saldo de su cuenta `por_pagar` en el libro). */
  porCobrar: 'drop-por-cobrar',
  /** Lo que el revendedor ya trajo del catálogo: la pestaña «Míos». */
  misProductos: 'drop-mis-productos',
  /** Los papeles que un negocio se ganó: revendedor, proveedor, almacén. */
  misPapeles: 'drop-mis-papeles',
  /** El domicilio y la operación del almacén propio. */
  miAlmacen: 'drop-mi-almacen',
  /** Stock con dueño y lugar: lo que tengo y dónde, lo que guardo y de quién. */
  stockPorAlmacen: 'drop-stock-por-almacen',
  /** La trazabilidad de un producto: el kardex. */
  kardex: 'drop-kardex',
  /** Remesas: aviso de envío del proveedor y recepción contada del almacén. */
  remesas: 'drop-remesas',
  /** Adónde se puede mandar mercadería: mis almacenes, donde tengo stock y los couriers que la guardan. */
  destinosDeRemesa: 'drop-destinos-de-remesa',
  /** Lo que puedo mandar: mis productos activos, publicados o no en Drop. */
  productosParaRemesa: 'drop-productos-para-remesa',
  /** Pedidos donde tengo parte, con el papel que juego en cada uno. */
  pedidosConParte: 'drop-pedidos-con-parte',
  /** Lo que el almacén necesita para despachar un encargo consignado. */
  paraDespachar: 'drop-para-despachar',
  /** Los revendedores a los que el proveedor puede invitar a un producto privado, y sus invitados. */
  misRevendedores: 'drop-mis-revendedores',
  invitados: 'drop-invitados',
  /** La liquidación: una fila por pedido, con lo que dejó por concepto (20260925172000). */
  liquidacion: 'drop-liquidacion',
  /** El reparto de una venta: lo que se llevó cada actor, del libro (20261028120000). */
  repartoDeLaVenta: 'drop-reparto-de-la-venta',
  /** El saldo de cada cuenta del negocio en el libro. */
  misSaldos: 'drop-mis-saldos',
  /** El estado de cuenta de un negocio en un papel y un periodo, cuadrado contra el libro (20261028009000). */
  estadoDeCuenta: 'drop-estado-de-cuenta',
  /** Lo emitido de un negocio en un papel: el archivo con su número correlativo (20261028009000). */
  estadosDeCuentaEmitidos: 'drop-estados-de-cuenta-emitidos',
  /** Smarty: cada actor con sus papeles, para sacar su estado de cuenta (20261028009000). */
  actoresParaEstadoDeCuenta: 'drop-actores-para-estado-de-cuenta',
  /** El por recibir partido en recaudado (lo tiene el courier) y en revisión (ya depositó), con sus ventas (20261026070000). */
  miPorRecibir: 'drop-mi-por-recibir',
  /** Cómo ve el negocio el flujo de su dinero: barra o torta (20261026080000). */
  graficoDelDinero: 'drop-grafico-del-dinero',
  /** Las tarifas de Smarty (recargo, comisión, fulfillment) y las del courier por distrito. */
  tarifas: 'drop-tarifas',
  tarifasDelCourier: 'drop-courier-tarifas',
  /** El mapa de un courier: sus zonas con la tarifa que ve quien pregunta (20260925176000). */
  /** Lo que le falta al negocio para operar en Drop, según sus papeles (20260925177000). */
  requisitos: 'drop-requisitos',
  /** Si Smarty ya aprobó la cuenta del drop que se registró solo (20261005170000). */
  miRevision: 'drop-mi-revision',
  /** Lo que Smarty le transfiere a cada negocio (20260925179000). */
  proximoPago: 'drop-proximo-pago',
  misPagos: 'drop-mis-pagos',
  pagosPorTransferir: 'drop-pagos-por-transferir',
  /** La rendición del día del courier y las declaradas que revisa Smarty (20260925180000). */
  miRendicion: 'drop-mi-rendicion',
  rendicionesDeclaradas: 'drop-rendiciones-declaradas',
  /** El historial de rendiciones, con sus vouchers (20261013180000). */
  historialDeRendiciones: 'drop-historial-de-rendiciones',
  /** Transferencias y POS que Smarty confirma (20260925181000). */
  cobrosPorConfirmar: 'drop-cobros-por-confirmar',
  /** Los descuentos adicionales de Smarty (20260925182000). */
  ajustesRecientes: 'drop-ajustes-recientes',
  negociosParaAjustar: 'drop-negocios-para-ajustar',
  /** Las comunidades de drops: las que afilia Smarty y la del drop (20260925189000). */
  comunidades: 'drop-comunidades',
  miComunidad: 'drop-mi-comunidad',
  /** El courier de Lima: su perfil, sus vínculos con negocios y sus motorizados (20260925169000). */
  perfilDeCourier: 'drop-courier-perfil',
  motorizados: 'drop-courier-motorizados',
  /** Lo que los couriers tienen por rendir (sólo Smarty). */
  porRendir: 'drop-courier-por-rendir',
  /** Los pagos de los couriers que publican su plata por API, con su conciliación (20261028130000; sólo Smarty). */
  pagosDeLosCouriers: 'drop-courier-pagos',
  /**
   * Las ventas propias por agencia que esperan su prepago (20260925197000) y el pago del despacho de
   * UNA venta (20260925194000). Vivían escritas a mano en su componente: fuera de acá, nada las
   * refrescaba cuando la plata cambiaba (auditoría, 26-sep).
   */
  despachosPorPrepagar: 'drop-despachos-por-prepagar',
  despachoDeLaVentaPropia: 'drop-despacho-venta-propia',
  /** El alta del proveedor (términos y estado) y la cola de Smarty para habilitarlos (20260925207000). */
  miAlta: 'drop-mi-alta',
  proveedoresPorRevisar: 'drop-proveedores-por-revisar',
  /** Los proveedores con el sello de verificado (20260928130000). */
  verificacionesDeProveedores: 'drop-verificaciones-de-proveedores',
} as const;

/** Una clave de caché del módulo Drop. Cualquier otra cosa no compila. */
export type ClaveDrop = (typeof CLAVES_DROP)[keyof typeof CLAVES_DROP];
